/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { createContext, useContext, useState } from 'react';
import createPlan from '../../../../domain/use_cases/plans/createPlan';
import AuthenticationContext from '../../auth/AuthenticationContext';
import { ICreditMap, } from '@leedpulse-monorepo/interfaces';
import { TPlanFeatures } from '@leedpulse-monorepo/types';
import { Currency, ECreditType, EProductType, PlanFeaturesType, ContextStatus, currencyAsString } from '@leedpulse-monorepo/enums';
import { IPlanPrice } from '@leedpulse-monorepo/interfaces';
import { IPlanLink } from '@leedpulse-monorepo/interfaces';

const PlanCreationFormContext = createContext({
  status: ContextStatus.IDLE,
  id: '',
  color: '',
  credits: Object.keys(ECreditType).reduce((acc, key) => {
    acc[key as ECreditType] = 0;
    return acc;
  }, {} as ICreditMap),
  features: Object.keys(PlanFeaturesType).reduce((acc, key) => {
    acc[key as PlanFeaturesType] = false;
    return acc;
  }, {} as TPlanFeatures),
  name: '',
  price: { currency: Currency.EUR, value: 0, formatted: `${0.00} ${currencyAsString(Currency.EUR)}`, stripe_price_id: '', stripe_tax_id: '' } as IPlanPrice,
  product: EProductType.LEEDPULSE,
  recurrence: false,
  recurrence_credits_reset: false,
  links: [] as IPlanLink[],
  downgradable_to: [] as string[],
  upgradable_to: [] as string[],
  payg: Object.keys(ECreditType).reduce((acc, key) => {
      acc[key as ECreditType] = 0;
      return acc;
    }, {} as ICreditMap),
  onIdChange: (_: string) => { },
  onColorChange: (_: string) => { },
  onCreditsChange: (_: ECreditType, __: number) => { },
  onFeaturesChange: (_: PlanFeaturesType, __: boolean) => { },
  onNameChange: (_: string) => { },
  onPriceChange: (_: Partial<IPlanPrice>) => { },
  onProductChange: (_: EProductType) => { },
  onRecurrenceChange: (_: boolean) => { },
  onRecurrenceCreditsResetChange: (_: boolean) => { },
  onDowngradableToChange: (_: string[]) => { },
  onUpgradableToChange: (_: string[]) => { },
  onPaygChange: (_: ECreditType, __: number) => { },
  submit: () => { },
});

export const PlanCreationFormProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [id, setId] = useState('');
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [color, setColor] = useState('');
  const [credits, setCredits] = useState<ICreditMap>(Object.keys(ECreditType).reduce((acc, key) => {
      acc[key as ECreditType] = 0;
      return acc;
    }, {} as ICreditMap));
  const [features, setFeatures] = useState<TPlanFeatures>(Object.keys(PlanFeaturesType).reduce((acc, key) => {
    acc[key as PlanFeaturesType] = false;
    return acc;
  }, {} as TPlanFeatures)
  );
  const [name, setName] = useState('');
  const [price, setPrice] = useState<IPlanPrice>({ currency: Currency.EUR, value: 0, formatted: `${0.00} ${currencyAsString(Currency.EUR)}`, stripe_price_id: '', stripe_tax_id: '' });
  const [product, setProduct] = useState(EProductType.LEEDPULSE);
  const [recurrence, setRecurrence] = useState(false);
  const [recurrenceCreditsReset, setRecurrenceCreditsReset] = useState(false);
  const [links, setLinks] = useState<IPlanLink[]>([]);
  const [downgradableTo, setDowngradableTo] = useState<string[]>([]);
  const [upgradableTo, setUpgradableTo] = useState<string[]>([]);
  const [payg, setPayg] = useState<ICreditMap>(Object.keys(ECreditType).reduce((acc, key) => {
      acc[key as ECreditType] = 0;
      return acc;
    }, {} as ICreditMap));
  const authContext = useContext(AuthenticationContext);

  const onIdChange = (id: string) => {
    setId(id);
  };

  const onColorChange = (color: string) => {
    setColor(color);
  };

  const onCreditsChange = (creditType: ECreditType, value: number) => {
    setCredits({
      ...credits,
      [creditType]: value,
    });
  };

  const onFeaturesChange = (featureType: PlanFeaturesType, value: boolean) => {
    setFeatures({
      ...features,
      [featureType]: value,
    });
  };

  const onNameChange = (name: string) => {
    setName(name);
  };

  const onPriceChange = (changes: Partial<IPlanPrice>) => {
    setPrice(
      o => {
        if (changes.formatted === undefined)
          changes.formatted = `${changes.value?.toFixed(2) ?? o.value.toFixed(2)} ${currencyAsString(changes.currency ?? o.currency)}`;

        return {
          ...o,
          ...changes
        }
      }
    );
  };

  const onProductChange = (product: EProductType) => {
    setProduct(product);
  };

  const onRecurrenceChange = (value: boolean) => {
    setRecurrence(value);
  };

  const onRecurrenceCreditsResetChange = (value: boolean) => {
    setRecurrenceCreditsReset(value);
  };

  const onDowngradableToChange = (value: string[]) => {
    setDowngradableTo(value);
  };

  const onUpgradableToChange = (value: string[]) => {
    setUpgradableTo(value);
  };

  const onPaygChange = (creditType: ECreditType, value: number) => {
    setPayg(o => ({
      ...o,
      [creditType]: value,
    }));
  };

  const submit = async () => {
    if (product === null || authContext.authentication === null) return;
    setStatus(ContextStatus.LOADING);
    try {
      await createPlan(
        {
          id,
          color,
          credits,
          features,
          name,
          price,
          product,
          recurrence,
          recurrenceCreditsReset,
          links: [],
          downgradableTo,
          upgradableTo,
          payg,
        },
        authContext.authentication
      );
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  return (
    <PlanCreationFormContext.Provider
      value={{
        status,
        id,
        color,
        credits,
        features,
        name,
        price,
        product,
        recurrence,
        recurrence_credits_reset: recurrenceCreditsReset,
        links,
        downgradable_to: downgradableTo,
        upgradable_to: upgradableTo,
        payg,
        onIdChange,
        onColorChange,
        onCreditsChange,
        onFeaturesChange,
        onNameChange,
        onPriceChange,
        onProductChange,
        onRecurrenceChange,
        onRecurrenceCreditsResetChange,
        onDowngradableToChange,
        onUpgradableToChange,
        onPaygChange,
        submit,
      }}
    >
      {children}
    </PlanCreationFormContext.Provider>
  );
};

export default PlanCreationFormContext;
